import { GraphQLClient } from "graphql-request";

import { getStoredAccessToken } from "@/lib/authStorage";
import { resolveGraphqlEndpoint } from "@/lib/resolveGraphqlEndpoint";
import {
  clearSessionAndRedirectToLogin,
  isSessionExpiryError,
} from "@/lib/sessionExpiry";
import {
  refreshSession,
  shouldRedirectAfterRefreshFailure,
} from "@/lib/sessionRefresh";

const endpoint = resolveGraphqlEndpoint();

function getClient(extraHeaders?: Record<string, string>) {
  const token = getStoredAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(extraHeaders || {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return new GraphQLClient(endpoint, { headers });
}

export function fetcher<
  TData,
  TVariables extends object = Record<string, never>,
>(
  query: string,
  variables?: TVariables,
  requestHeaders?: Record<string, string>,
) {
  return async (context?: { signal?: AbortSignal }): Promise<TData> => {
    const request = async () => {
      const client = getClient(requestHeaders);
      return await client.request<TData, TVariables>({
        document: query as any,
        variables: (variables ?? ({} as TVariables)) as TVariables,
        signal: context?.signal,
      } as any);
    };

    try {
      return await request();
    } catch (err) {
      if (isSessionExpiryError(err)) {
        const refreshResult = await refreshSession();
        if (refreshResult.ok && !refreshResult.skipped) {
          try {
            return await request();
          } catch (retryErr) {
            if (isSessionExpiryError(retryErr)) {
              clearSessionAndRedirectToLogin();
            }
            throw retryErr;
          }
        }
        if (shouldRedirectAfterRefreshFailure(refreshResult)) {
          clearSessionAndRedirectToLogin();
        }
      }
      throw err;
    }
  };
}
